<script setup lang="ts">
import { Menu, X } from 'lucide-vue-next'
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '~/stores/auth'

const router = useRouter()
const authStore = useAuthStore()
const isScrolled = ref(false)
const isMenuOpen = ref(false)
const activeSection = ref('home')
const isNavbarHidden = ref(false)
let prevScrollPos = 0

// Navigation items
const navigation = [
  { id: 'home', title: 'Home' },
  { id: 'fitur', title: 'Fitur' },
  { id: 'harga', title: 'Harga' },
  { id: 'faq', title: 'FAQ' },
]

// Handle scroll to section with smooth behavior
function scrollToSection(sectionId: string) {
  const element = document.getElementById(sectionId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    isMenuOpen.value = false
  }
}

// Handle scroll event for sticky nav and hide-on-scroll
function handleScroll() {
  const currentScrollPos = window.pageYOffset

  // Hide on scroll logic
  if (prevScrollPos > currentScrollPos) {
    isNavbarHidden.value = false
  }
  else if (currentScrollPos > 30) {
    isNavbarHidden.value = true
  }

  prevScrollPos = currentScrollPos

  // Sticky nav logic
  isScrolled.value = window.scrollY > 30

  // Update active section based on scroll position
  const sections = navigation.map(nav => nav.id)
  for (const sectionId of sections) {
    const element = document.getElementById(sectionId)
    if (element) {
      const rect = element.getBoundingClientRect()
      if (rect.top <= 100 && rect.bottom >= 100) {
        activeSection.value = sectionId
        break
      }
    }
  }
}

// Handle escape key to close mobile menu
function handleEscapeKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && isMenuOpen.value) {
    isMenuOpen.value = false
  }
}

// Watch menu open state to toggle body scroll
watch(isMenuOpen, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  }
  else {
    document.body.style.overflow = ''
  }
})

onMounted(() => {
  prevScrollPos = window.pageYOffset
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('keydown', handleEscapeKey)
  handleScroll() // Initial check
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('keydown', handleEscapeKey)
  document.body.style.overflow = '' // Clean up body overflow
})

function goToLogin() {
  router.push('/masuk')
}

function goToRegister() {
  router.push('/daftar')
}

function goToHome() {
  router.push('/')
}

function goToDashboard() {
  router.push('/dashboard')
}
</script>

<template>
  <div
    class="fixed top-0 w-full z-40 transition-transform duration-500 bg-white"
    :class="{
      'shadow-sm': isScrolled,
    }"
  >
    <nav class="container mx-auto relative z-20 flex justify-between items-center py-5 px-4">
      <!-- Logo -->
      <div
        class="flex-none cursor-pointer"
        @click="goToHome"
      >
        <img
          class="w-8 h-8 object-contain"
          src="/images/icons/iconRintisanSmall.png"
          alt="Rintisan Logo"
        >
      </div>

      <!-- Desktop Navigation -->
      <div class="hidden md:flex m-auto">
        <ul class="flex items-center gap-8">
          <li
            v-for="section in navigation"
            :key="section.id"
            class="px-4 py-1 transition-all duration-300 ease-in-out rounded-full"
            :class="{
              'bg-accent': activeSection === section.id,
            }"
          >
            <a
              :href="`#${section.id}`"
              class="text-base transition-all duration-300 ease-in-out"
              :class="{
                'text-neutral-900 font-bold': activeSection === section.id,
                'text-neutral-900': activeSection !== section.id,
              }"
              @click.prevent="scrollToSection(section.id)"
            >
              {{ section.title }}
            </a>
          </li>
        </ul>
      </div>

      <!-- Desktop Auth Buttons -->
      <ClientOnly>
        <div v-if="!authStore.isAuthenticated" class="hidden md:flex flex-none items-center gap-3">
          <Button
            variant="ghost"
            size="lg"
            @click="goToLogin"
          >
            Masuk
          </Button>
          <Button
            class="bg-gradient-rintisan text-white"
            size="lg"
            @click="goToRegister"
          >
            Coba Sekarang
          </Button>
        </div>
        <div v-else class="hidden md:block">
          <Button variant="ghost" size="lg" @click="goToDashboard">
            Dashboard
          </Button>
        </div>
      </ClientOnly>

      <!-- Mobile Menu Button -->
      <ClientOnly>
        <Button
          variant="ghost"
          size="icon"
          class="md:hidden"
          @click="isMenuOpen = !isMenuOpen"
        >
          <Menu v-if="!isMenuOpen" class="size-6" />
          <X v-else class="size-6" />
        </Button>
      </ClientOnly>
    </nav>

    <ClientOnly>
      <Transition name="fade">
        <div
          v-if="isMenuOpen"
          class="fixed top-[72px] left-0 right-0 bg-white z-40 md:hidden shadow-lg border-t"
        >
          <div class="flex flex-col divide-y">
            <div
              v-for="section in navigation"
              :key="section.id"
              class="text-center "
            >
              <a
                :href="`#${section.id}`"
                class="block text-base font-medium transition-colors py-6"
                :class="{
                  'text-primary': activeSection === section.id,
                }"
                @click.prevent="scrollToSection(section.id)"
              >
                {{ section.title }}
              </a>
            </div>

            <div v-if="!authStore.isAuthenticated" class="flex flex-col gap-4 px-4 py-8">
              <Button
                size="lg"
                class="w-full"
                variant="gradient"
                @click="goToRegister"
              >
                Coba Sekarang
              </Button>
              <Button
                variant="ghost"
                size="lg"
                class="w-full"
                @click="goToLogin"
              >
                Masuk
              </Button>
            </div>
            <div v-else>
              <Button variant="ghost" size="lg" class="w-full h-auto py-6" @click="goToDashboard">
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </Transition>
    </ClientOnly>
  </div>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 300ms ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide down transition for mobile menu */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
